import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Truck, MapPin, Package, MoreHorizontal } from 'lucide-react';
import { StatCard, WidgetPanel } from './DashboardShared';
import { deliveriesOf, deliveryFeesOf } from '../../../data/store';

export const DeliveryModule = ({ org }) => {
  const metrics = useMemo(() => {
    const deliveries = deliveriesOf(org.id);
    const fees = deliveryFeesOf(org.id);
    const today = new Date().toDateString();

    let todayCount = 0;
    let pendingCount = 0;
    let totalFees = 0;

    deliveries.forEach(d => {
      if (new Date(d.date || d.createdAt).toDateString() === today) todayCount++;
      if (d.status !== 'delivered' && d.status !== 'cancelled') pendingCount++;
    });
    
    // Simplistic sum of configured delivery fees as a placeholder for total fee revenue
    fees.forEach(f => {
      totalFees += (Number(f.fee) || 0);
    });

    const recent = [...deliveries].sort((a,b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)).slice(0, 5);

    return { todayCount, pendingCount, totalFees, recent, deliveries };
  }, [org.id]);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.15em] text-[#0d0d0d] border-b border-[var(--theme-tertiary)] pb-3 mt-4">
        <Truck size={16} className="text-[var(--theme-primary)]" />
        Delivery Management
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={Truck} label="Deliveries Today" value={metrics.todayCount} iconColor="#3b82f6" />
        <StatCard icon={Package} label="Pending Deliveries" value={metrics.pendingCount} suffix="awaiting / transit" iconColor="#f59e0b" />
        <StatCard icon={MapPin} label="Delivery Fees Configured" value={`₹${metrics.totalFees.toFixed(2)}`} iconColor="#059669" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <WidgetPanel title="Recent Deliveries" className="lg:col-span-1">
          <ul className="divide-y divide-[var(--theme-tertiary)]">
            {metrics.recent.length === 0 && <li className="py-4 text-center text-xs font-semibold uppercase tracking-[0.14em] text-slate-300">No deliveries yet</li>}
            {metrics.recent.map((d, idx) => (
              <li key={idx} className="py-2.5 flex items-center justify-between text-xs group cursor-pointer hover:bg-slate-50 px-2 -mx-2 rounded transition-colors">
                <div>
                  <p className="font-bold text-[#0d0d0d] truncate max-w-[150px]">{d.customerName || d.orderId}</p>
                  <p className="text-[10px] font-medium text-slate-400 mt-0.5">{new Date(d.createdAt).toLocaleString()}</p>
                </div>
                <span className={`font-bold uppercase tracking-widest text-[10px] px-2 py-0.5 rounded ${
                  d.status === 'delivered' ? 'bg-emerald-100 text-emerald-700' :
                  d.status === 'dispatched' ? 'bg-amber-100 text-amber-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {d.status}
                </span>
              </li>
            ))}
          </ul>
        </WidgetPanel>

        <WidgetPanel title="Delivery Status Board" className="lg:col-span-2">
           <div className="grid grid-cols-3 gap-3 h-full">
             <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex flex-col gap-2">
               <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Pending</h4>
               <div className="text-2xl font-bold text-slate-700">{metrics.deliveries.filter(d => d.status === 'new' || d.status === 'preparing').length}</div>
             </div>
             <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex flex-col gap-2">
               <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-amber-600 border-b border-amber-200 pb-2">In Transit</h4>
               <div className="text-2xl font-bold text-amber-700">{metrics.deliveries.filter(d => d.status === 'dispatched').length}</div>
             </div>
             <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex flex-col gap-2">
               <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-600 border-b border-emerald-200 pb-2">Delivered</h4>
               <div className="text-2xl font-bold text-emerald-700">{metrics.deliveries.filter(d => d.status === 'delivered').length}</div>
             </div>
           </div>
        </WidgetPanel>
      </div>
    </div>
  );
};

DeliveryModule.propTypes = {
  org: PropTypes.object.isRequired,
};
